/**
 * React wrapper for Bryntum DisplayField
 */
import React, { RefObject } from 'react';
import { AlignSpec, Base, ContainerItemConfig, DisplayField, DomConfig, Field, FieldContainer, FieldContainerConfig, FieldTriggerConfig, MaskConfig, Model, Rectangle, Scroller, ScrollerConfig, TabConfig, TooltipConfig, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumDisplayFieldProps =  {
     // Configs only
    adopt?                   : HTMLElement|string
    align?                   : AlignSpec|string
    anchor?                  : boolean
    ariaDescription?         : string
    ariaLabel?               : string
    autoComplete?            : string
    autoSelect?              : boolean
    bubbleEvents?            : object
    callOnFunctions?         : boolean
    centered?                : boolean
    clearable?               : boolean|Partial<FieldTriggerConfig>
    config?                  : object
    constrainTo?             : HTMLElement|Widget|Rectangle
    container?               : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Partial<FieldContainerConfig>|FieldContainer
    containValues?           : boolean|string|((field: Field) => boolean)
    contentElementCls?       : string|object
    defaultBindProperty?     : string
    dock?                    : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?               : boolean|{
        handleSelector?: string
    }
    floating?                : boolean
    hideAnimation?           : boolean|object
    highlightExternalChange? : boolean
    hint?                    : string|((data: { source: Field, value: any }) => string)
    hintHtml?                : string|((data: { source: Field, value: any }) => string)
    htmlCls?                 : string|object
    ignoreParentReadOnly?    : boolean
    inline?                  : boolean
    inputAlign?              : string
    inputAttributes?         : object
    inputType?               : string
    inputWidth?              : string|number
    keyStrokeChangeDelay?    : number
    labelCls?                : string|object
    labelPosition?           : 'before'|'above'
    labels?                  : object[]
    labelWidth?              : string|number
    listeners?               : object
    localeClass?             : typeof Base
    localizable?             : boolean
    localizableProperties?   : string[]
    maskDefaults?            : Partial<MaskConfig>
    masked?                  : boolean|string|Partial<MaskConfig>
    monitorResize?           : boolean
    name?                    : string
    owner?                   : Widget
    placeholder?             : string
    positioned?              : boolean
    preventTooltipOnTouch?   : boolean
    relayStoreEvents?        : boolean
    required?                : boolean
    revertOnEscape?          : boolean
    ripple?                  : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?             : ShadowRoot
    scrollAction?            : 'hide'|'realign'|null
    showAnimation?           : boolean|object
    showTooltipWhenDisabled? : boolean
    spellCheck?              : boolean
    tab?                     : boolean|Partial<TabConfig>
    tag?                     : string
    template?                : (fieldConfig: Partial<ContainerItemConfig>) => DomConfig|string|void
    textAlign?               : 'left'|'center'|'right'|'start'|'end'
    title?                   : string
    ui?                      : string|object
    validateOnInput?         : boolean
    weight?                  : number

     // Configs and properties
    alignSelf?        : string
    appendTo?         : HTMLElement|string
    badge?            : string
    cls?              : string|object
    content?          : string
    dataset?          : object
    disabled?         : boolean
    editable?         : boolean
    extraData?        : string|number|boolean|object
    flex?             : number|string
    height?           : number|string
    hidden?           : boolean
    html?             : string|((widget: Widget) => string)
    id?               : string
    insertBefore?     : HTMLElement|string
    insertFirst?      : HTMLElement|string
    label?            : string
    margin?           : number|string
    maxHeight?        : string|number
    maximizeOnMobile? : number|string
    maxWidth?         : string|number
    minHeight?        : string|number
    minWidth?         : string|number
    readOnly?         : boolean
    scrollable?       : Scroller|boolean|Partial<ScrollerConfig>
    tooltip?          : string|Partial<TooltipConfig>
    triggers?         : Record<string, Widget>|Record<string, FieldTriggerConfig>
    value?            : any|string
    width?            : number|string
    x?                : number
    y?                : number

     // Properties only
    anchorSize? : number[]
    input?      : HTMLElement
    type?       : string

     // Events
    onAction?        : ((event: { source: Field|any, value: string|number|boolean|any, oldValue: string|number|boolean|any, valid: boolean, event: Event, record: Model, records: Model[], userAction: boolean }) => void)|string
    onBeforeDestroy? : ((event: { source: Base }) => void)|string
    onBeforeHide?    : ((event: { source: Widget }) => boolean|void)|string
    onBeforeShow?    : ((event: { source: Widget|any }) => Promise<boolean|void>)|string
    onCatchAll?      : ((event: {[key: string]: any, type: string}) => void)|string
    onChange?        : ((event: { source: Field|any, value: string|number|boolean|any, oldValue: string|number|boolean|any, valid: boolean, event: Event, userAction: boolean }) => void)|string
    onClear?         : ((event: { source: Field|any }) => void)|string
    onDestroy?       : ((event: { source: Base }) => void)|string
    onFocusIn?       : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?      : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onHide?          : ((event: { source: Widget }) => void)|string
    onInput?         : ((event: { source: Field|any, value: string|number|boolean|any, event: Event }) => void)|string
    onPaint?         : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onReadOnly?      : ((event: { readOnly: boolean }) => void)|string
    onRecompose?     : (() => void)|string
    onResize?        : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onShow?          : ((event: { source: Widget }) => void)|string
    onTrigger?       : ((event: { source: Field|any, trigger: Widget }) => void)|string

}

export class BryntumDisplayField extends React.Component<BryntumDisplayFieldProps> {

    static instanceClass = DisplayField;

    static instanceName = 'DisplayField';

    processWidgetContent = processWidgetContent;

    static configNames = [
        'adopt',
        'align',
        'anchor',
        'ariaDescription',
        'ariaLabel',
        'autoComplete',
        'autoSelect',
        'bubbleEvents',
        'callOnFunctions',
        'centered',
        'clearable',
        'config',
        'constrainTo',
        'container',
        'containValues',
        'contentElementCls',
        'defaultBindProperty',
        'dock',
        'draggable',
        'floating',
        'hideAnimation',
        'highlightExternalChange',
        'hint',
        'hintHtml',
        'htmlCls',
        'ignoreParentReadOnly',
        'inline',
        'inputAlign',
        'inputAttributes',
        'inputType',
        'inputWidth',
        'keyStrokeChangeDelay',
        'labelCls',
        'labelPosition',
        'labels',
        'labelWidth',
        'listeners',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maskDefaults',
        'masked',
        'monitorResize',
        'name',
        'owner',
        'placeholder',
        'positioned',
        'preventTooltipOnTouch',
        'relayStoreEvents',
        'required',
        'revertOnEscape',
        'ripple',
        'rootElement',
        'scrollAction',
        'showAnimation',
        'showTooltipWhenDisabled',
        'spellCheck',
        'tab',
        'tag',
        'template',
        'textAlign',
        'title',
        'ui',
        'validateOnInput',
        'weight'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'appendTo',
        'badge',
        'cls',
        'content',
        'dataset',
        'disabled',
        'editable',
        'extraData',
        'flex',
        'height',
        'hidden',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'label',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'minHeight',
        'minWidth',
        'onAction',
        'onBeforeDestroy',
        'onBeforeHide',
        'onBeforeShow',
        'onCatchAll',
        'onChange',
        'onClear',
        'onDestroy',
        'onFocusIn',
        'onFocusOut',
        'onHide',
        'onInput',
        'onPaint',
        'onReadOnly',
        'onRecompose',
        'onResize',
        'onShow',
        'onTrigger',
        'readOnly',
        'scrollable',
        'tooltip',
        'triggers',
        'value',
        'width',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'input',
        'type'
    ];

    // Component instance
    instance!: DisplayField;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumDisplayFieldProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-display-field-container`;
        return (
            <div className={className} ref={(element) => (this.element = element!)}></div>
        );

    }
}
